import React, { useState, useEffect } from 'react';
import { AIWaveResponse } from './types';
import { Code, Check, AlertCircle, Copy, ChevronDown } from 'lucide-react';

interface ConfigEditorProps {
  data: AIWaveResponse;
  onUpdate: (data: AIWaveResponse) => void;
}

const ConfigEditor: React.FC<ConfigEditorProps> = ({ data, onUpdate }) => {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!isDirty) {
      setText(JSON.stringify(data, null, 2));
      setError(null);
    }
  }, [data, isDirty]);

  const handleApply = () => {
    try {
      const parsed = JSON.parse(text);
      if (!parsed.config || !parsed.emitters) throw new Error("Missing required config or emitters fields");
      setError(null);
      setIsDirty(false);
      onUpdate(parsed);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-md flex flex-col gap-3">
       <div className="flex items-center justify-between text-gray-600">
        <div
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 hover:text-blue-600 transition-colors cursor-pointer"
        >
            <Code size={14} className="text-blue-600" />
            <h3 className="font-bold text-xs tracking-wider uppercase text-gray-800">Simulation Config</h3>
            <ChevronDown 
                size={14} 
                className={`text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
        </div>
        <div className="flex items-center gap-2">
            {error && <span className="text-red-600 text-[10px] flex items-center gap-1"><AlertCircle size={11}/> Invalid</span>}
            <button onClick={handleCopy} className="hover:text-blue-600 transition-colors text-gray-500" title="Copy to clipboard">
                {copied ? <Check size={12} className="text-green-600"/> : <Copy size={12}/>}
            </button>
        </div>
       </div>
       {isExpanded && (
       <>
       <textarea 
         className="bg-gray-50 border border-gray-300 rounded p-2 text-[11px] font-mono text-gray-800 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none leading-tight h-96"
         value={text}
         onChange={(e) => setText(e.target.value)}
          onFocus={() => setIsDirty(true)}
         spellCheck={false}
       />
       <button 
        onClick={handleApply}
        className="bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-bold py-2 rounded flex items-center justify-center gap-2 transition-colors border border-gray-300"
       >
        <Check size={12} />
        Apply
       </button>
       </>
       )}
    </div>
  )
}

export default ConfigEditor;
